import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const contactCards = [
  { icon: "ri-map-pin-2-fill", label: "Registered Office", value: "One Embankment", sub: "Leeds LS1 4DW, United Kingdom", color: "#C9A84C" },
  { icon: "ri-phone-fill", label: "Telephone", value: "+44 20 3097 6185", sub: "+1 437 292 2296", color: "#C9A84C" },
  { icon: "ri-mail-fill", label: "Investment Enquiries", value: "invest@thorpedellow.com", sub: "Response within 24 hours", color: "#C9A84C" },
  { icon: "ri-time-fill", label: "Office Hours", value: "Mon–Fri: 9am–5pm", sub: "Excluding UK Bank Holidays", color: "#C9A84C" },
];

const subjects = [
  "Social Housing Structured Product Enquiry",
  "Expression of Interest",
  "NASDAQ Dubai Bond Enquiry",
  "Investor Eligibility Question",
  "Existing Investor Support",
  "Partnership / Referral Opportunity",
  "Press & Media",
  "General Enquiry",
];

const steps = [
  { num: "01", icon: "ri-mail-check-line", title: "We Receive Your Message", desc: "Your enquiry is instantly logged and assigned to a member of our investment relations team." },
  { num: "02", icon: "ri-user-voice-line", title: "Initial Call (Within 24hrs)", desc: "A senior investment manager will contact you by phone or email within one business day to discuss your requirements." },
  { num: "03", icon: "ri-file-text-line", title: "Documentation Provided", desc: "You will receive the relevant Information Memorandum, investor eligibility forms, and supporting materials for review." },
  { num: "04", icon: "ri-pen-nib-line", title: "Expression of Interest", desc: "Once satisfied, you may complete an Expression of Interest and begin the formal onboarding process." },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "", investorType: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    if (form.message.length > 500) {
      setError("Message must be 500 characters or fewer.");
      return;
    }
    setError("");
    setSubmitting(true);
    const body = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d740vdb41deorl02ek40", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#EEF2F9]">
      <Navbar />

      {/* Hero */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: 420,
          backgroundImage: "url('https://readdy.ai/api/search-image?query=elegant%20London%20financial%20district%2C%20City%20of%20London%20office%20towers%20at%20golden%20hour%2C%20glass%20and%20steel%20architecture%2C%20warm%20amber%20light%20on%20facades%2C%20professional%20corporate%20atmosphere%2C%20wide%20cinematic%20view%2C%20no%20people%2C%20timeless%20British%20business%20district&width=1400&height=420&seq=809&orientation=landscape')",
          backgroundSize: "cover",
          backgroundPosition: "center 40%",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(7,26,46,0.94) 0%, rgba(7,26,46,0.80) 55%, rgba(7,26,46,0.65) 100%)" }} />
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "linear-gradient(to bottom, transparent, #C9A84C 25%, #E2C27A 55%, #C9A84C 80%, transparent)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-16">
          <span className="inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/30 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
            <i className="ri-customer-service-2-line text-xs leading-none" />
            Investment Relations
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get in Touch
          </h1>
          <p className="text-white/65 text-sm md:text-base max-w-xl leading-relaxed">
            Speak directly with our investment team about the Thorpe Dellow Social Housing Structured Product or any of our listed offerings.
          </p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 md:-mt-10 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {contactCards.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl px-5 md:px-6 py-4 md:py-5 flex items-start gap-3 md:gap-4" style={{ boxShadow: "0 4px 24px rgba(7,26,46,0.08)" }}>
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: "rgba(201,168,76,0.12)" }}>
                <i className={`${c.icon} text-base md:text-lg leading-none`} style={{ color: c.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-0.5">{c.label}</p>
                <p className="text-[#071A2E] text-sm font-semibold leading-snug truncate">{c.value}</p>
                <p className="text-slate-400 text-xs mt-0.5">{c.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">

          {/* LEFT — Office info + map + what to expect */}
          <div className="flex flex-col gap-8">
            {/* Our Office */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#071A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Our Office</h2>
              <div className="flex flex-col gap-5 mb-8">
                {[
                  { icon: "ri-map-pin-2-line", label: "Address", value: "One Embankment, Leeds LS1 4DW, United Kingdom" },
                  { icon: "ri-phone-line", label: "Telephone (UK)", value: "+44 20 3097 6185", href: "tel:+442030976185" },
                  { icon: "ri-phone-line", label: "Telephone (Intl)", value: "+1 437 292 2296", href: "tel:+14372922296" },
                  { icon: "ri-mail-line", label: "Email", value: "invest@thorpedellow.com", href: "mailto:invest@thorpedellow.com" },
                  { icon: "ri-time-line", label: "Office Hours", value: "Monday to Friday, 9:00am – 5:00pm GMT" },
                  { icon: "ri-global-line", label: "Client Portal", value: "portal.thorpedellow.com", href: "https://portal.thorpedellow.com" },
                ].map((o) => (
                  <div key={o.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#EEF2F9] text-[#C9A84C] flex-shrink-0">
                      <i className={`${o.icon} text-lg leading-none`} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-0.5">{o.label}</p>
                      {o.href ? (
                        <a href={o.href} target={o.href.startsWith("http") ? "_blank" : undefined} rel="nofollow noreferrer" className="text-sm font-medium text-[#071A2E] hover:text-[#C9A84C] transition-colors cursor-pointer">{o.value}</a>
                      ) : (
                        <p className="text-sm font-medium text-[#071A2E]">{o.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Google Map */}
              <div className="w-full h-56 rounded-xl overflow-hidden border border-slate-100">
                <iframe
                  title="Thorpe Dellow Office"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2353.0!2d-1.5491!3d53.7997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48795c2a6c0fc0e5%3A0x0!2sOne+Embankment%2C+Leeds+LS1+4DW!5e0!3m2!1sen!2suk!4v1711000000002"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                />
              </div>
            </div>

            {/* What Happens Next */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#071A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>What Happens Next</h2>
              <div className="flex flex-col gap-5">
                {steps.map((s, i) => (
                  <div key={s.num} className="flex items-start gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl text-[#C9A84C]" style={{ background: "rgba(201,168,76,0.10)" }}>
                        <i className={`${s.icon} text-lg leading-none`} />
                      </div>
                      {i < steps.length - 1 && <div className="w-px h-5 bg-slate-100 mt-2" />}
                    </div>
                    <div className="pt-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-[#C9A84C] font-bold tracking-widest">{s.num}</span>
                        <h3 className="text-sm font-bold text-[#071A2E]">{s.title}</h3>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div>
            <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 4px 32px rgba(7,26,46,0.07)" }}>
              {/* Form header */}
              <div className="px-8 py-6 border-b border-slate-100" style={{ background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 100%)" }}>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/20 border border-[#C9A84C]/30">
                    <i className="ri-send-plane-2-line text-[#C9A84C] text-base leading-none" />
                  </div>
                  <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Send a Message</h2>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed">Complete the form below and a member of our team will respond within one business day.</p>
              </div>

              {submitted ? (
                <div className="text-center py-16 px-8">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-5" style={{ background: "radial-gradient(circle, rgba(201,168,76,0.18) 0%, rgba(201,168,76,0.04) 100%)", border: "1.5px solid rgba(201,168,76,0.4)" }}>
                    <i className="ri-check-double-line text-[#C9A84C] text-3xl leading-none" />
                  </div>
                  <h3 className="font-bold text-[#071A2E] text-2xl mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Message Received</h3>
                  <p className="text-slate-500 text-sm max-w-xs mx-auto leading-relaxed">Thank you for reaching out. A member of our investment relations team will contact you within one business day.</p>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                    <a href="/social-housing" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
                      View Investment Product
                    </a>
                    <button 
                      type="button"
                      onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#C9A84C] text-[#071A2E] text-sm font-semibold hover:bg-[#B8953B] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-calendar-check-line leading-none" />
                      Schedule a Call
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="px-8 py-7 flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Full Name <span className="text-[#C9A84C]">*</span></label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Jane Smith" className="w-full bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Email Address <span className="text-[#C9A84C]">*</span></label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="jane@example.com" className="w-full bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Phone Number</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+44 (0)7700 000000" className="w-full bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Company / Organisation</label>
                      <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Firm name (optional)" className="w-full bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Investor Classification</label>
                    <select name="investorType" value={form.investorType} onChange={handleChange} className="w-full bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors cursor-pointer">
                      <option value="">Select classification (optional)</option>
                      <option>Certified High Net Worth Individual</option>
                      <option>Self-Certified Sophisticated Investor</option>
                      <option>Retail Investor</option>
                      <option>Family Office / UHNW</option>
                      <option>IFA / Financial Adviser</option>
                      <option>Institutional Investor</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Subject <span className="text-[#C9A84C]">*</span></label>
                    <select name="subject" value={form.subject} onChange={handleChange} required className="w-full bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors cursor-pointer">
                      <option value="">Select a subject</option>
                      {subjects.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">
                      Message <span className="text-[#C9A84C]">*</span>
                      <span className="font-normal text-slate-400 ml-1">(max 500 characters)</span>
                    </label>
                    <textarea name="message" value={form.message} onChange={handleChange} required rows={5} maxLength={500} placeholder="Please describe how we can assist you. The more detail you provide, the more efficiently our team can respond..." className="w-full bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors resize-none" />
                    <p className="text-right text-xs text-slate-400 mt-1">{form.message.length}/500</p>
                  </div>

                  {/* Disclaimer */}
                  <div className="bg-[#EEF2F9] border border-slate-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-slate-500 leading-relaxed">
                      <strong className="text-slate-600">Data Protection:</strong> Your information is processed in accordance with UK GDPR. Thorpe Dellow will use your details solely to respond to your enquiry. We will not share your data with third parties without your consent. Capital at risk. Section 21 FSMA 2000 compliant.
                    </p>
                  </div>

                  {error && (
                    <p className="text-red-500 text-xs bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2">
                      <i className="ri-error-warning-line leading-none flex-shrink-0" />
                      {error}
                    </p>
                  )}

                  <button type="submit" disabled={submitting} className="w-full py-4 rounded-xl bg-[#071A2E] text-white font-semibold text-sm hover:bg-[#0D2B4A] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2">
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin leading-none" /> Sending...</>
                    ) : (
                      <><i className="ri-send-plane-2-line leading-none" /> Send Message</>
                    )}
                  </button>

                  {/* Trust strip */}
                  <div className="flex items-center justify-center flex-wrap gap-3 md:gap-6 pt-1 border-t border-slate-50">
                    {[
                      { icon: "ri-shield-check-line", text: "Secure & Encrypted" },
                      { icon: "ri-lock-2-line", text: "UK GDPR Compliant" },
                      { icon: "ri-bank-line", text: "FSMA 2000 Regulated" },
                    ].map((b) => (
                      <div key={b.text} className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`${b.icon} text-[#C9A84C] text-xs leading-none`} />
                        </div>
                        <span className="text-[10px] text-slate-400 whitespace-nowrap">{b.text}</span>
                      </div>
                    ))}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory strip */}
      <section style={{ background: "#071A2E" }} className="py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-slate-500 text-[11px] leading-relaxed text-center max-w-4xl mx-auto">
            <strong className="text-slate-400">Regulatory Notice:</strong> All communications are issued in accordance with Section 21 of the Financial Services and Markets Act 2000 (FSMA). This website and all financial promotions are directed solely at Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors. Investments are speculative and involve risk. Capital may be at risk. Past performance is not indicative of future results.
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
